import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import TrackArt from '../TrackArt';
import AutomixCover from './AutomixCover';
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg';
import { ReactComponent as FolderIcon } from '../../assets/icons/select-folder.svg';
import { closeSet, clearSource } from '../../redux/slices/automixSlice';
import { closeAutomixSet } from '../../redux/thunk/appNavThunk';
import { chooseSetCover, clearSetCover } from '../../redux/thunk/automixThunk';
import { addTrack, resetAudioList, setAutoPlay } from '../../redux/slices/musicPlayerSlice';
import { track as trackEvent } from '../../utils/analytics';
import { formatClock, formatLength, timelineSegments, trackIndexAt, transitionHint, transitionLabel } from '../../utils/automixFormat';

const localArt = (p) => `http://localhost:3069/album-art?path=${encodeURIComponent(p)}`;

// same pencil the set name uses on the setup screen
const PencilIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M4 20h4L19 9l-4-4L4 16z" /><path d="M13.5 6.5l4 4" />
    </svg>
);
const CrossIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" focusable="false">
        <path d="M6 6l12 12M18 6L6 18" />
    </svg>
);
// How far before a blend "Listen" drops the needle, so the ear hears the
// outgoing track alone first.
const TRANSITION_LEAD_SEC = 15;

/**
 * The finished set. It plays through the app's own player (so the player bar,
 * volume and media keys all work); this view follows that player's clock and
 * seeks it — the timeline, the tracklist and every "Listen" are the same seek.
 */
const AutomixSetView = () => {
    const dispatch = useDispatch();
    const set = useSelector((s) => s.automix.set);
    const hasSource = useSelector((s) => !!s.automix.source);
    const playerPlaying = useSelector((s) => s.musicPlayer.isPlaying);
    const [audioUrl, setAudioUrl] = useState(null);
    const [playerCover, setPlayerCover] = useState(null);
    const [active, setActive] = useState(false);   // the player is on THIS set
    const [position, setPosition] = useState(0);
    const [hover, setHover] = useState(null);      // {x, sec} over the timeline
    const pendingSeek = useRef(null);
    const timelineRef = useRef(null);

    const total = Number(set?.durationSec) || 0;
    const segments = useMemo(() => timelineSegments(set), [set]);

    useEffect(() => {
        let alive = true;
        window.electron.getAudioUrl(set.mp3).then((u) => { if (alive) setAudioUrl(u); });
        // the player paints covers through CSS url(): this bridge escapes for that
        const art = set.artPaths?.[0];
        if (art) window.electron.getAlbumArtUrl(art).then((u) => { if (alive) setPlayerCover(u); }).catch(() => {});
        return () => { alive = false; };
    }, [set.mp3, set.artPaths]);

    // Follow the app player: is it on this set, and where.
    useEffect(() => {
        if (!audioUrl) return undefined;
        const isOurs = (src) => typeof src === 'string' && src.split('#')[0] === audioUrl;
        const sync = () => setActive(isOurs(window.__currentPlayerMusicSrc));
        sync();
        const onChange = (e) => setActive(isOurs(e.detail?.musicSrc));
        window.addEventListener('player-track-change', onChange);
        return () => window.removeEventListener('player-track-change', onChange);
    }, [audioUrl]);

    useEffect(() => {
        if (!active) { setPosition(0); return undefined; }
        const tick = () => {
            const audio = window.musicPlayerInstance;
            if (!audio) return;
            // a seek asked for before the player had loaded the set
            if (pendingSeek.current != null && audio.duration > 0) {
                audio.currentTime = pendingSeek.current;
                pendingSeek.current = null;
            }
            setPosition(audio.currentTime || 0);
        };
        tick();
        const id = setInterval(tick, 250);
        return () => clearInterval(id);
    }, [active]);

    const playing = active && playerPlaying;
    const nowIdx = active ? trackIndexAt(set, position) : -1;

    const playFrom = useCallback((sec, from) => {
        const at = Math.max(0, Math.min(total ? total - 1 : sec, sec));
        const audio = window.musicPlayerInstance;
        if (active && audio) {
            audio.currentTime = at;
            audio.play?.();
        } else if (audioUrl) {
            pendingSeek.current = at > 0 ? at : null;
            dispatch(resetAudioList());
            dispatch(addTrack({
                name: set.name,
                singer: `MixGoat Automix · ${set.trackCount} tracks`,
                cover: playerCover || undefined,
                musicSrc: audioUrl,
            }));
            dispatch(setAutoPlay(true));
        }
        trackEvent('automix_set_played', { from, at_sec: Math.round(at), track_count: set.trackCount });
    }, [active, audioUrl, playerCover, dispatch, set, total]);

    const togglePlay = () => {
        const audio = window.musicPlayerInstance;
        if (active && audio) {
            if (playing) audio.pause(); else audio.play?.();
        } else playFrom(0, 'hero');
    };

    const secAtPointer = (e) => {
        const rect = timelineRef.current.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        return { x: ratio * rect.width, sec: ratio * total };
    };

    const hoverSeg = hover ? segments[trackIndexAt(set, hover.sec)] : null;
    const created = set.createdAt ? new Date(set.createdAt) : null;

    const backLabel = hasSource ? 'Back to the playlist' : 'Back to your sets';
    const shortLabel = hasSource ? 'Playlist' : 'Your sets';
    const goBack = () => dispatch(closeAutomixSet());
    const Chevron = () => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    );

    return (
        <div className="amx-set">
            <div className="amx-crumb-row">
                <button type="button" className="amx-crumb" onClick={goBack} title={backLabel}>
                    <Chevron />
                    {shortLabel}
                </button>
            </div>
            <header className="amx-set-hero">
                <div className="amx-set-cover">
                    <AutomixCover set={set} size={132} />
                    {/* Always on show: hover-only was invisible — nothing said the
                        artwork could be changed at all. */}
                    <span className="amx-cover-tools">
                        <button
                            type="button"
                            className="amx-cover-btn"
                            title="Change the cover"
                            aria-label="Change the cover"
                            onClick={() => dispatch(chooseSetCover())}
                        >
                            <PencilIcon />
                        </button>
                        {set.cover && (
                            <button
                                type="button"
                                className="amx-cover-btn"
                                title="Remove the cover"
                                aria-label="Remove the cover"
                                onClick={() => dispatch(clearSetCover())}
                            >
                                <CrossIcon />
                            </button>
                        )}
                    </span>
                </div>
                <div className="amx-set-hero-text">
                    <span className="amx-eyebrow">Automix set</span>
                    <h1 title={set.name}>{set.name}</h1>
                    <p className="amx-set-meta">
                        {[
                            formatLength(total),
                            `${set.trackCount} tracks`,
                            `${set.transitionCount} transitions`,
                            created && !Number.isNaN(created.getTime()) ? created.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '',
                        ].filter(Boolean).join(' · ')}
                    </p>
                    <div className="amx-set-actions">
                        <Button className="button main-button amx-play-btn" onClick={togglePlay} disabled={!audioUrl}>
                            {playing ? <PauseIcon className="icon" aria-hidden="true" /> : <PlayIcon className="icon" aria-hidden="true" />}
                            {playing ? 'Pause' : active ? 'Resume' : 'Play set'}
                        </Button>
                        <Button
                            className="button choose-button"
                            onClick={() => {
                                window.electron.shell.showItemInFolder(set.mp3);
                                trackEvent('automix_set_revealed', { track_count: set.trackCount });
                            }}
                        >
                            <FolderIcon className="icon" aria-hidden="true" />
                            Show in Finder
                        </Button>
                        <Button className="button choose-button" onClick={() => { dispatch(closeSet()); dispatch(clearSource()); }}>
                            New set
                        </Button>
                    </div>
                </div>
            </header>

            {(set.warnings?.length > 0 || set.skipped > 0) && (
                <p className="amx-set-note">
                    {set.skipped > 0 && `${set.skipped} ${set.skipped === 1 ? 'track was' : 'tracks were'} left out (no audio file on this computer). `}
                    {set.warnings?.length > 0 && `${set.warnings.length === 1 ? 'One pair' : `${set.warnings.length} pairs`} couldn’t be blended cleanly and ${set.warnings.length === 1 ? 'was' : 'were'} cut instead.`}
                </p>
            )}

            {total > 0 && (
                <section className="amx-timeline-wrap" aria-label="Set timeline">
                    <div
                        className="amx-timeline"
                        ref={timelineRef}
                        onMouseMove={(e) => setHover(secAtPointer(e))}
                        onMouseLeave={() => setHover(null)}
                        onClick={(e) => playFrom(secAtPointer(e).sec, 'timeline')}
                        role="presentation"
                    >
                        {segments.map((seg) => (
                            <div
                                key={seg.idx}
                                className={`amx-seg${seg.idx === nowIdx ? ' is-now' : ''}${seg.idx % 2 ? ' is-alt' : ''}`}
                                style={{ left: `${(seg.start / total) * 100}%`, width: `${((seg.end - seg.start) / total) * 100}%` }}
                            >
                                {seg.blendStart != null && (
                                    <span
                                        className="amx-seg-blend"
                                        style={{ width: `${((seg.end - seg.blendStart) / Math.max(1, seg.end - seg.start)) * 100}%` }}
                                    />
                                )}
                                <span className="amx-seg-n">{seg.idx + 1}</span>
                            </div>
                        ))}
                        {active && <span className="amx-playhead" style={{ left: `${(position / total) * 100}%` }} />}
                        {hover && <span className="amx-hoverline" style={{ left: hover.x }} />}
                    </div>
                    <div className="amx-timeline-scale">
                        <span>{active ? formatClock(position) : '0:00'}</span>
                        {hover && hoverSeg ? (
                            <span className="amx-timeline-hover">
                                {formatClock(hover.sec)} · {hoverSeg.track.artist ? `${hoverSeg.track.artist} – ` : ''}{hoverSeg.track.title}
                            </span>
                        ) : <span className="amx-timeline-legend"><i /> blend</span>}
                        <span>{formatClock(total)}</span>
                    </div>
                </section>
            )}

            <section className="amx-set-list">
                {set.tracks.map((t, i) => {
                    const tr = set.transitions.find((x) => x.index === i);
                    return (
                        <React.Fragment key={`${t.idx}-${t.path}`}>
                            <div
                                className={`amx-set-row${i === nowIdx ? ' is-now' : ''}`}
                                onDoubleClick={() => playFrom(t.startSec || 0, 'tracklist')}
                            >
                                <span className="col-n">{i + 1}</span>
                                <button type="button" className="amx-time-btn" onClick={() => playFrom(t.startSec || 0, 'tracklist')} title="Play from here">
                                    {formatClock(t.startSec || 0)}
                                </button>
                                <span className="col-title">
                                    <TrackArt className="amx-row-cover" src={t.path ? localArt(t.path) : null} size={32} />
                                    <span className="amx-row-text">
                                        <span className="amx-row-name" title={t.title}>{t.title}</span>
                                        <span className="amx-row-artist" title={t.artist}>{t.artist || '—'}</span>
                                    </span>
                                </span>
                                <span className="col-num">{t.bpm ? Math.round(t.bpm) : '—'}</span>
                                <span className="col-num">{t.camelot || '—'}</span>
                            </div>
                            {tr && (
                                <div className="amx-transition" title={transitionHint(tr.strategy)}>
                                    <span className="amx-transition-line" aria-hidden="true" />
                                    <span className="amx-transition-chip">{transitionLabel(tr.strategy)}</span>
                                    <span className="amx-transition-meta">
                                        {tr.bars > 0 ? `${tr.bars} bars · ` : ''}{formatClock(tr.startSec)}
                                    </span>
                                    <button
                                        type="button"
                                        className="amx-link"
                                        onClick={() => playFrom((tr.startSec || 0) - TRANSITION_LEAD_SEC, 'transition')}
                                    >
                                        Listen
                                    </button>
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </section>
        </div>
    );
};

export default AutomixSetView;
